<template>
  <nav class="tabs" v-if="tabs">
    <button
      class="tab"
      :class="{ selected: selected === index }"
      v-for="(tab, index) in tabs"
      :style="tabStyle(tab)"
      @click="selectTab(index)"
    >
      <h4 :style="fontStyle(tab)">{{ ls(tab.title) }}</h4>
      <p :style="fontStyle(tab)">{{ ls(tab.subTitle) }}</p>
    </button>
  </nav>
</template>

<script lang="ts" setup>
const { ls } = useI18n()
const { $isMobile } = useNuxtApp()

interface Props {
  tabs: PartTabContent[]
  selected: number
}
const props = withDefaults(defineProps<Props>(), {})

const emits = defineEmits(['select'])

const tabStyle = (tab: PartTabContent) => {
  let style = {}
  if (tab.color) {
    const textColor = chooseTextColor(tab.color.substring(1))
    style = { 'background-color': tab.color, color: textColor }
  }

  if ($isMobile.value) {
    if (tab.width) {
      style['width'] = `auto`
    }
  } else {
    if (tab.width) {
      style['width'] = `${tab.width}%`
    }
  }
  return style
}

const fontStyle = (tab: PartTabContent) => {
  let style = {}
  if (tab.color) {
    const textColor = chooseTextColor(tab.color.substring(1))
    style = { color: textColor }
  }
  return style
}

const selectTab = index => {
  emits('select', index)
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.tabs {
  display: flex;
  width: 100%;
  overflow: hidden;
  @include is-mobile() {
    flex-direction: column;
    gap: 2px;
  }

  @include is-tablet() {
    gap: 2px;
    &:first-child {
      border-radius: 4px 4px 4px 8px;
    }
    &:last-child {
      border-radius: 4px 8px 8px 4px;
    }
  }

  .tab {
    @include no-decorative-button;
    flex-grow: 1;
    background-color: $jps-gray-600;
    text-align: left;
    border-radius: 3px;
    color: $white;
    transition: 0.3s;
    position: relative;
    z-index: 0;
    @include is-mobile() {
      padding: 0.5rem 1.5rem;
      margin-left: 0.5rem;
    }
    @include is-tablet() {
      margin-top: 0.5rem;
      padding: 0.5rem 0.75rem;
    }
    &.selected {
      @include is-mobile() {
        margin-left: 0;
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
      }
      @include is-tablet() {
        margin-top: 0rem;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        border-bottom-right-radius: 4px;
      }
    }
    h4 {
      font-size: 1rem;
      font-weight: bold;
      color: $white;
    }
    p {
      font-size: 0.75rem;
      color: $white;
    }
    &:after {
      content: '';
      width: 100%;
      height: 100%;
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      z-index: -1;
      background-color: rgba(0, 0, 0, 1);
      mix-blend-mode: overlay;
      opacity: 0;
      transition: 0.3s;
      @include is-mobile() {
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
      }
      @include is-tablet() {
        border-radius: 8px 8px 4px 4px;
      }
    }
    &.selected:after {
      opacity: 0.5;
    }
    &:hover:after {
      opacity: 0.25;
    }
  }
  tab.selected {
    border: 1px solid red;
  }
}
</style>
